import React, { useState, useEffect, useRef, useMemo } from 'react';

// --- UTILS & HOOKS ---

// Hook for "Hacker" Text Scramble Effect
const useScrambleText = (text, speed = 30) => {
  const [displayedText, setDisplayedText] = useState(text);
  const iterations = useRef(0);

  useEffect(() => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*";
    const interval = setInterval(() => {
      setDisplayedText(prev => 
        text
          .split("")
          .map((letter, index) => {
            if (index < iterations.current) return text[index];
            return letters[Math.floor(Math.random() * 26)];
          })
          .join("")
      );
      
      if (iterations.current >= text.length) clearInterval(interval);
      iterations.current += 1 / 3;
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed]);

  return displayedText;
};

// Hook for Scroll Animations
const useOnScreen = (options) => {
  const ref = useRef(null);
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, options);

    if (ref.current) observer.observe(ref.current);

    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [ref, options]);

  return [ref, isVisible];
};

// --- COMPONENTS ---

// 3D Tilt Card Component
const TiltCard = ({ children, className }) => {
  const [rotate, setRotate] = useState({ x: 0, y: 0 });
  
  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const box = card.getBoundingClientRect();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    const centerX = box.width / 2;
    const centerY = box.height / 2;
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;

    setRotate({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotate({ x: 0, y: 0 });
  };

  return (
    <div 
      className={`transition-transform duration-200 ease-out transform preserve-3d ${className}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${rotate.x}deg) rotateY(${rotate.y}deg) scale3d(1, 1, 1)`,
      }}
    >
      {children}
    </div>
  );
};

// Neural Network 3D Scene
const NeuralNetworkScene = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    let scene, camera, renderer, animationId;
    let particles, lines;

    const init = () => {
      if (!mountRef.current || !window.THREE) return;
      const THREE = window.THREE;

      // Setup
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      
      // Cleanup previous canvas
      while (mountRef.current.firstChild) mountRef.current.removeChild(mountRef.current.firstChild);
      mountRef.current.appendChild(renderer.domElement);

      // Create Neural Nodes (Particles)
      const particleCount = 120;
      const particleGeo = new THREE.BufferGeometry();
      const particlePos = new Float32Array(particleCount * 3);
      const velocities = [];

      for(let i = 0; i < particleCount; i++) {
        const x = (Math.random() - 0.5) * 15;
        const y = (Math.random() - 0.5) * 15;
        const z = (Math.random() - 0.5) * 15;
        particlePos[i * 3] = x;
        particlePos[i * 3 + 1] = y;
        particlePos[i * 3 + 2] = z;
        velocities.push({
          x: (Math.random() - 0.5) * 0.02,
          y: (Math.random() - 0.5) * 0.02,
          z: (Math.random() - 0.5) * 0.02
        });
      }

      particleGeo.setAttribute('position', new THREE.BufferAttribute(particlePos, 3));
      const particleMat = new THREE.PointsMaterial({
        color: 0x22d3ee, // Cyan
        size: 0.1,
        transparent: true,
        opacity: 0.8
      });
      particles = new THREE.Points(particleGeo, particleMat);
      scene.add(particles);

      // Lines Material
      const lineMat = new THREE.LineBasicMaterial({
        color: 0x22d3ee,
        transparent: true,
        opacity: 0.15
      });

      camera.position.z = 10;

      // Mouse Interaction
      let mouseX = 0;
      let mouseY = 0;
      
      const onMouseMove = (event) => {
        mouseX = (event.clientX / window.innerWidth) * 2 - 1;
        mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
      };
      window.addEventListener('mousemove', onMouseMove);

      // Animation Loop
      const animate = () => {
        animationId = requestAnimationFrame(animate);
        
        // Rotate entire system slowly
        particles.rotation.y += 0.002;
        particles.rotation.x += 0.001;

        // Mouse influence
        particles.rotation.x += mouseY * 0.001;
        particles.rotation.y += mouseX * 0.001;

        // Draw connections dynamically
        // Note: Re-calculating lines every frame is heavy, but fine for <150 points
        // For production apps with more points, use shaders or predetermined paths
        const positions = particles.geometry.attributes.position.array;
        const linePositions = [];
        
        // Update positions based on velocity
        for(let i = 0; i < particleCount; i++) {
          positions[i*3] += velocities[i].x;
          positions[i*3+1] += velocities[i].y;
          positions[i*3+2] += velocities[i].z;

          // Boundary check
          if(Math.abs(positions[i*3]) > 8) velocities[i].x *= -1;
          if(Math.abs(positions[i*3+1]) > 8) velocities[i].y *= -1;
          if(Math.abs(positions[i*3+2]) > 8) velocities[i].z *= -1;
        }
        particles.geometry.attributes.position.needsUpdate = true;

        // Find connections
        for(let i = 0; i < particleCount; i++) {
          for(let j = i + 1; j < particleCount; j++) {
            const dx = positions[i*3] - positions[j*3];
            const dy = positions[i*3+1] - positions[j*3+1];
            const dz = positions[i*3+2] - positions[j*3+2];
            const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);

            if (dist < 3.5) {
              linePositions.push(
                positions[i*3], positions[i*3+1], positions[i*3+2],
                positions[j*3], positions[j*3+1], positions[j*3+2]
              );
            }
          }
        }

        // Update Lines
        if (lines) scene.remove(lines);
        const lineGeo = new THREE.BufferGeometry();
        lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
        lines = new THREE.LineSegments(lineGeo, lineMat);
        // Apply same rotation as particles to keep them synced
        lines.rotation.copy(particles.rotation);
        scene.add(lines);

        renderer.render(scene, camera);
      };
      animate();

      const handleResize = () => {
        if (!mountRef.current) return;
        camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
      };
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('resize', handleResize);
        cancelAnimationFrame(animationId);
        if (renderer.domElement) renderer.domElement.remove();
      };
    };

    if (!window.THREE) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
      script.onload = init;
      document.body.appendChild(script);
    } else {
      init();
    }
  }, []);

  return <div ref={mountRef} className="absolute inset-0 z-0 opacity-40 pointer-events-none" />;
};

// Icon Components (Reused for stability)
const IconWrapper = ({ size = 20, className, children, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>{children}</svg>
);
const Github = p => <IconWrapper {...p}><path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"/><path d="M9 18c-4.51 2-5-2-7-2"/></IconWrapper>;
const Linkedin = p => <IconWrapper {...p}><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></IconWrapper>;
const Mail = p => <IconWrapper {...p}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></IconWrapper>;
const ExternalLink = p => <IconWrapper {...p}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></IconWrapper>;
const ChevronDown = p => <IconWrapper {...p}><path d="m6 9 6 6 6-6"/></IconWrapper>;
const Menu = p => <IconWrapper {...p}><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></IconWrapper>;
const X = p => <IconWrapper {...p}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></IconWrapper>;

// --- MAIN PORTFOLIO COMPONENT ---

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Style Injection
  useEffect(() => {
    if (!document.getElementById('tailwind-script')) {
      const s = document.createElement('script');
      s.id = 'tailwind-script';
      s.src = "https://cdn.tailwindcss.com";
      document.head.appendChild(s);
    }
    if (!document.getElementById('google-fonts')) {
      const l = document.createElement('link');
      l.id = 'google-fonts';
      l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  // Scroll Handling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      const sections = ['home', 'about', 'skills', 'experience', 'projects', 'contact'];
      const current = sections.find(s => {
        const el = document.getElementById(s);
        if(el) {
          const r = el.getBoundingClientRect();
          return r.top >= -100 && r.top <= 400;
        }
        return false;
      });
      if(current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    setIsMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const nameText = useScrambleText("Craig Dsouza");

  // Data
  const projects = [
    {
      title: "Synthetica AI",
      tag: "NLP / ML",
      desc: "Production-grade text classifier achieving 99.67% accuracy using DistilBERT & Logistic Regression.",
      tech: ["Python", "Flask", "PyTorch"],
      highlight: "99.67% Acc"
    },
    {
      title: "NeuroNav",
      tag: "Computer Vision",
      desc: "Real-time pothole detection system utilizing sensor fusion and telemetry-enabled inference.",
      tech: ["OpenCV", "TensorFlow", "IoT"],
      highlight: "Award Winner"
    },
    {
      title: "SFIT Assets",
      tag: "Full Stack",
      desc: "Enterprise asset tracking platform for 1500+ users with automated ETL and PowerBI-style analytics.",
      tech: ["React", "Node.js", "SQL"],
      highlight: "1500+ Users"
    },
    {
      title: "GIL Refiner",
      tag: "GenAI",
      desc: "LLM-driven prompt engineering tool that reduces hallucinations and improves context retention.",
      tech: ["Gemini API", "spaCy", "React"],
      highlight: "Published"
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-slate-300 font-sans selection:bg-cyan-500/30 overflow-x-hidden" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
      
      {/* 3D Background - Persistent */}
      <div className="fixed inset-0 pointer-events-none">
        <NeuralNetworkScene />
        {/* Vignette Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/0 via-[#050505]/0 to-[#050505] z-10" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#050505_120%)] z-10" />
      </div>

      {/* Navbar */}
      <nav className={`fixed w-full z-50 transition-all duration-500 border-b ${scrolled ? 'bg-[#050505]/80 backdrop-blur-xl border-white/5 py-4' : 'bg-transparent border-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="text-xl font-bold tracking-tighter text-white flex items-center gap-2 cursor-pointer group" onClick={() => scrollTo('home')}>
            <span className="text-cyan-400 group-hover:animate-pulse font-mono">{`{`}</span>
            CD
            <span className="text-cyan-400 group-hover:animate-pulse font-mono">{`}`}</span>
          </div>

          <div className="hidden md:flex gap-10">
            {['About', 'Skills', 'Experience', 'Projects'].map((item) => (
              <button 
                key={item}
                onClick={() => scrollTo(item.toLowerCase())}
                className={`text-sm font-medium tracking-wide transition-all duration-300 hover:text-cyan-400 relative group ${activeSection === item.toLowerCase() ? 'text-cyan-400' : 'text-slate-400'}`}
              >
                {item}
                <span className={`absolute -bottom-1 left-0 h-[1px] bg-cyan-400 transition-all duration-300 ${activeSection === item.toLowerCase() ? 'w-full' : 'w-0 group-hover:w-full'}`} />
              </button>
            ))}
          </div>

          <button 
            onClick={() => scrollTo('contact')}
            className="hidden md:block px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#050505] bg-cyan-400 hover:bg-cyan-300 transition-colors rounded-sm"
          >
            Hire Me
          </button>

          {/* Mobile Menu Toggle */}
          <button className="md:hidden text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Nav Drawer */}
        <div className={`md:hidden absolute top-full left-0 w-full bg-[#0a0a0a] border-b border-white/10 overflow-hidden transition-all duration-300 ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          {['About', 'Skills', 'Experience', 'Projects', 'Contact'].map((item) => (
             <button key={item} onClick={() => scrollTo(item.toLowerCase())} className="block w-full text-left px-6 py-4 text-slate-400 hover:text-cyan-400 hover:bg-white/5 border-l-2 border-transparent hover:border-cyan-400 transition-all">
               {item}
             </button>
          ))}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-20 px-6 z-20">
        <div className="max-w-7xl w-full grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/5 border border-white/10 rounded-full backdrop-blur-md animate-fade-in-up">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
              </span>
              <span className="text-xs font-mono text-cyan-400 tracking-wider">SYSTEM ONLINE</span>
            </div>

            <div>
              <h1 className="text-6xl md:text-8xl font-bold text-white tracking-tighter leading-none mb-4">
                {nameText}
                <span className="text-cyan-500 animate-pulse">_</span>
              </h1>
              <p className="text-xl md:text-2xl text-slate-400 font-light tracking-wide max-w-lg">
                AI/ML Engineer & Full-Stack Architect.
                <br />
                <span className="text-cyan-400/80 text-lg font-mono mt-2 block">
                  Building intelligent systems for the future.
                </span>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button onClick={() => scrollTo('projects')} className="group relative px-8 py-4 bg-white text-black font-bold text-sm uppercase tracking-widest overflow-hidden">
                <span className="relative z-10 group-hover:text-white transition-colors duration-300">View Work</span>
                <div className="absolute inset-0 bg-cyan-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300 ease-out" />
              </button>
              <div className="flex gap-4 items-center pl-4">
                {[
                    { Icon: Github, href: "https://github.com/CraigMLdsouza" },
                    { Icon: Linkedin, href: "https://linkedin.com/in/craig-ml-dsouza" },
                    { Icon: Mail, href: "mailto:dsouzacraigmichael@gmail.com" }
                ].map(({ Icon, href }, i) => (
                  <a key={i} href={href} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-cyan-400 transition-colors transform hover:scale-110">
                    <Icon size={24} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Hero Decorative / Stats */}
          <div className="hidden lg:flex justify-end relative">
             <TiltCard className="relative w-80 h-96 bg-gradient-to-br from-white/10 to-white/5 border border-white/10 backdrop-blur-md rounded-xl p-8 flex flex-col justify-between">
                <div>
                   <div className="text-xs font-mono text-slate-500 mb-2">CURRENT STATUS</div>
                   <div className="text-2xl text-white font-bold">Open for Work</div>
                </div>
                
                <div className="space-y-4">
                  {[
                    { label: "ML Models", val: "Production Ready" },
                    { label: "Stack", val: "Full Cycle" },
                    { label: "Location", val: "Mumbai, IN" }
                  ].map((stat, i) => (
                    <div key={i} className="flex justify-between items-center border-b border-white/10 pb-2">
                       <span className="text-sm text-slate-400">{stat.label}</span>
                       <span className="text-sm font-mono text-cyan-400">{stat.val}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4 flex justify-between items-end">
                   <div className="w-12 h-12 rounded bg-cyan-500/20 flex items-center justify-center">
                     <span className="text-2xl">⚡</span>
                   </div>
                   <span className="text-6xl font-bold text-white/5 absolute bottom-4 right-4">24</span>
                </div>
             </TiltCard>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
           <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Scroll</span>
           <ChevronDown className="text-cyan-400" size={16} />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 relative z-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <h2 className="text-4xl font-bold text-white mb-6">About<span className="text-cyan-400">.</span></h2>
              <div className="h-1 w-20 bg-cyan-500 mb-8" />
              <p className="text-slate-400 leading-relaxed text-lg">
                I bridge the gap between complex <span className="text-cyan-400">Machine Learning</span> models and practical, scalable applications. 
                My approach is outcome-driven, focusing on latency, reliability, and real-world impact.
              </p>
            </div>
            <div className="md:col-span-8 grid sm:grid-cols-2 gap-6">
               {[
                 { title: "Education", sub: "B.E. Computer Engineering", date: "2026", org: "St. Francis Institute of Technology" },
                 { title: "Achievement", sub: "National Winner", date: "2023", org: "Smart India Hackathon" },
                 { title: "Focus", sub: "RAG & LLMs", date: "Current", org: "Predictive Analysis" },
                 { title: "Architecture", sub: "Microservices", date: "Exp", org: "Scalable API Design" }
               ].map((card, i) => (
                 <div key={i} className="bg-white/[0.02] border border-white/5 p-6 hover:bg-white/[0.05] hover:border-cyan-500/30 transition-all duration-300 group">
                    <div className="flex justify-between items-start mb-4">
                       <span className="text-xs font-mono text-slate-500 border border-slate-800 px-2 py-1 rounded group-hover:text-cyan-400 group-hover:border-cyan-500/30 transition-colors">{card.date}</span>
                       <ExternalLink size={14} className="text-slate-600 group-hover:text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-1">{card.sub}</h3>
                    <p className="text-sm text-slate-400 mb-2">{card.title}</p>
                    <p className="text-xs font-mono text-slate-500">{card.org}</p>
                 </div>
               ))}
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-32 bg-[#080808] relative z-20 border-y border-white/5">
         <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16">
               <div>
                  <h2 className="text-4xl font-bold text-white mb-2">Technical Arsenal</h2>
                  <p className="text-slate-400">Tools employed for intelligent system construction.</p>
               </div>
               <div className="hidden md:block h-[1px] flex-1 bg-white/10 mx-8 mb-2" />
               <div className="text-xs font-mono text-cyan-400">STACK_V_2.0</div>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
               {[
                 { cat: "AI & ML", skills: ["LLMs", "RAG Systems", "DistilBERT", "TensorFlow", "PyTorch", "NLP", "Forecasting"] },
                 { cat: "Backend", skills: ["FastAPI", "Flask", "Node.js", "Microservices", "REST APIs", "Python"] },
                 { cat: "Cloud Ops", skills: ["AWS Bedrock", "SageMaker", "Lambda", "Docker", "CI/CD", "GPU Inference"] },
                 { cat: "Data", skills: ["Pinecone", "MongoDB", "Supabase", "SQL", "ETL Pipelines", "QuickSight"] }
               ].map((group, idx) => (
                 <div key={idx} className="space-y-6">
                    <h3 className="text-lg font-bold text-white border-b border-white/10 pb-4 flex items-center gap-2">
                       <span className="w-2 h-2 bg-cyan-500 rounded-sm" />
                       {group.cat}
                    </h3>
                    <ul className="space-y-3">
                       {group.skills.map(skill => (
                          <li key={skill} className="flex items-center gap-3 text-sm text-slate-400 group cursor-default">
                             <span className="h-[1px] w-3 bg-slate-700 group-hover:bg-cyan-400 group-hover:w-5 transition-all" />
                             <span className="group-hover:text-white transition-colors">{skill}</span>
                          </li>
                       ))}
                    </ul>
                 </div>
               ))}
            </div>
         </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-32 relative z-20">
        <div className="max-w-5xl mx-auto px-6">
           <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white">Experience Log</h2>
           </div>

           <div className="relative border-l border-white/10 ml-4 md:ml-0 space-y-16">
              {[
                { 
                  role: "Software Developer", company: "Western Railways", date: "May '25 - Jun '25", 
                  desc: "Engineered agentic AI pipelines to automate reporting, reducing manual effort by 35%. Implemented drift detection for deployed LLMs." 
                },
                { 
                  role: "Web Developer", company: "Nashabandimandal", date: "Jan '24 - Jul '24", 
                  desc: "Re-architected full-stack systems, boosting engagement by 30%. Introduced analytics dashboards using Node.js and Supabase." 
                },
                { 
                  role: "Lead Software Developer", company: "Aakriti Exports", date: "Sep '23 - Jan '24", 
                  desc: "Delivered the company's first digital platform, increasing conversions by 20%. Established CI/CD workflows with Docker." 
                }
              ].map((exp, i) => (
                <div key={i} className="relative pl-12 md:pl-16 group">
                   {/* Timeline Node */}
                   <div className="absolute left-[-5px] top-2 w-[11px] h-[11px] bg-[#050505] border border-slate-600 rounded-full group-hover:border-cyan-400 group-hover:scale-125 transition-all duration-300 z-10" />
                   
                   <div className="flex flex-col sm:flex-row sm:items-baseline justify-between mb-2">
                      <h3 className="text-2xl font-bold text-white group-hover:text-cyan-400 transition-colors">{exp.role}</h3>
                      <span className="font-mono text-xs text-slate-500 bg-white/5 px-3 py-1 rounded">{exp.date}</span>
                   </div>
                   <div className="text-lg text-slate-300 font-medium mb-4">{exp.company}</div>
                   <p className="text-slate-400 leading-relaxed max-w-3xl">{exp.desc}</p>
                </div>
              ))}
           </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section id="projects" className="py-32 bg-[#080808] border-t border-white/5 relative z-20">
         <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-white mb-16">Featured Deployments</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
               {projects.map((p, i) => (
                 <TiltCard key={i} className="group relative bg-[#050505] border border-white/10 p-8 hover:border-cyan-500/50 transition-colors h-full flex flex-col">
                    <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity text-cyan-500">
                       <ExternalLink size={20} />
                    </div>
                    
                    <div className="flex items-center gap-3 mb-6">
                       <span className="text-xs font-mono text-cyan-400 bg-cyan-900/20 px-2 py-1 rounded border border-cyan-500/20">{p.tag}</span>
                       <span className="text-xs font-mono text-slate-500">{p.highlight}</span>
                    </div>

                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">{p.title}</h3>
                    <p className="text-slate-400 mb-8 flex-grow leading-relaxed">{p.desc}</p>

                    <div className="flex flex-wrap gap-2 pt-6 border-t border-white/5">
                       {p.tech.map(t => (
                          <span key={t} className="text-xs text-slate-500 font-mono">#{t}</span>
                       ))}
                    </div>
                 </TiltCard>
               ))}
            </div>
         </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-32 relative z-20 overflow-hidden">
         <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-8 tracking-tighter">
               Ready to <span className="text-cyan-400">Scale?</span>
            </h2>
            <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
               I'm actively seeking opportunities to apply my expertise in AI/ML and Full-Stack Engineering. Let's build something robust.
            </p>
            
            <a 
              href="mailto:dsouzacraigmichael@gmail.com"
              className="inline-block px-10 py-5 bg-white text-black font-bold text-lg uppercase tracking-widest hover:bg-cyan-400 transition-colors shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)] hover:shadow-[0_0_40px_-5px_rgba(34,211,238,0.6)]"
            >
               Initialize Contact
            </a>

            <div className="mt-24 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center text-xs font-mono text-slate-600">
               <div>© 2025 CRAIG M. L. DSOUZA</div>
               <div className="flex gap-8 mt-4 md:mt-0">
                  <a href="https://github.com/CraigMLdsouza" target="_blank" rel="noreferrer" className="hover:text-cyan-400 transition-colors">GITHUB_REF</a>
                  <a href="https://linkedin.com/in/craig-ml-dsouza" target="_blank" rel="noreferrer" className="hover:text-cyan-400 transition-colors">LINKEDIN_REF</a>
               </div>
            </div>
         </div>
      </section>
    </div>
  );
};

export default Portfolio;